import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isExplicitlyClearedUserValue } from "@/utils/sanitizeValue";

/**
 * One user edit of a single attribute, newest-first. `data.user_data` is
 * append-only, so every edit is its own row — unlike the widget's `get_user_data`
 * (which collapses to the latest value per attribute), `get_user_data_history`
 * returns the full timeline for one attribute (WHA-3720).
 */
export interface AttributeHistoryEntry {
  attributeValue: string | null;
  editedBy: number | null;
  editedByName: string | null;
  editedByAvatarUrl: string | null;
  editedByColor: string | null;
  createdAt: string;
}

/** Raw RPC row shape from `public.get_user_data_history`. */
interface HistoryRow {
  attribute_value: string | null;
  edited_by: number | null;
  edited_by_name: string | null;
  edited_by_avatar_url: string | null;
  edited_by_color: string | null;
  created_at: string;
}

export const ATTRIBUTE_HISTORY_QUERY_KEY = "attribute-history" as const;

/**
 * Lazily fetches the full user-edit history for one building attribute. Only
 * runs while `enabled` (the history modal is open) and the org/building ids are
 * present — the caller already gates on `useOrganizationContext().isReady`
 * before rendering the sidebar, so a present `orgId` is a ready org.
 */
export function useAttributeHistory(
  buildingId: string | undefined,
  orgId: string | undefined,
  attributeKey: string,
  enabled: boolean
) {
  return useQuery<AttributeHistoryEntry[]>({
    queryKey: [ATTRIBUTE_HISTORY_QUERY_KEY, buildingId, orgId, attributeKey],
    enabled: enabled && !!buildingId && !!orgId && !!attributeKey,
    queryFn: async (): Promise<AttributeHistoryEntry[]> => {
      if (!buildingId || !orgId) return [];
      const { data, error } = await supabase.rpc(
        "get_user_data_history" as never,
        {
          p_org_id: orgId,
          p_building_id: parseInt(buildingId, 10),
          p_attribute_key: attributeKey,
        } as never
      );
      if (error) {
        console.warn("[attribute-history] query failed", error);
        throw error;
      }
      const rows = (data as unknown as HistoryRow[]) ?? [];
      return rows.map((r) => ({
        attributeValue: isExplicitlyClearedUserValue(r.attribute_value) ? null : r.attribute_value,
        editedBy: r.edited_by,
        editedByName: r.edited_by_name,
        editedByAvatarUrl: r.edited_by_avatar_url,
        editedByColor: r.edited_by_color,
        createdAt: r.created_at,
      }));
    },
  });
}
